import {
  addDoc,
  collection,
  type CollectionReference,
  doc,
  type DocumentData,
  type DocumentReference,
  getDoc,
  setDoc,
} from '@firebase/firestore';

// TODO: Fix this once the firebase types are consolidated in the root
// See: https://www.notion.so/Move-Firebase-related-stuff-to-top-level-in-app-router-14dfa9bf6a1c8082b849fd18bfd2a516?pvs=4
import { FirestoreNotFoundError } from '@app/api/errors/firebase';

import { isString } from '@app/utils/string';

import { getDb } from './config/getFirebaseClient';
import ClientTimestampConverter from './utils/converter';

export const getCollectionRef = <T extends DocumentData>(path: string, ...pathSegments: string[]) =>
  collection(getDb(), path, ...pathSegments).withConverter(new ClientTimestampConverter<T>());

export const getNewDocRef = <T extends DocumentData>(
  pathOrRef: string | CollectionReference<T>,
) => {
  const ref = isString(pathOrRef) ? getCollectionRef(pathOrRef) : pathOrRef;
  return doc(ref).withConverter(new ClientTimestampConverter<T>());
};

export const getPathDocRef = <T extends DocumentData>(path: string, ...pathSegments: string[]) =>
  doc(getDb(), path, ...pathSegments).withConverter(new ClientTimestampConverter<T>());

type GetDocumentParams<T> =
  | {
      path: string;
      id: string;
    }
  | { ref: DocumentReference<T> };

const getDocumentDataByRef = async <T extends DocumentData>(ref: DocumentReference<T>) => {
  const document = await getDoc<T>(ref);
  if (!document.exists()) {
    throw new FirestoreNotFoundError({ path: ref.path });
  }
  return document.data();
};

const getDocumentDataByPath = async <T extends DocumentData>(path: string, id: string) => {
  const document = await getDoc(getPathDocRef<T>(path, id));
  if (!document.exists()) {
    throw new FirestoreNotFoundError({ path });
  }
  return document.data();
};

export const getDocumentData = async <T extends DocumentData>(params: GetDocumentParams<T>) => {
  if ('ref' in params) {
    return getDocumentDataByRef(params.ref);
  }
  const { path, id } = params;
  return getDocumentDataByPath<T>(path, id);
};

type SetDocumentOptions = {
  merge?: boolean;
};
export const setDocument = <T extends DocumentData>(
  path: string,
  id: string,
  data: T,
  options: SetDocumentOptions = { merge: true },
) => setDoc(getPathDocRef<T>(path, id), data, options);

export const addDocument = <T extends DocumentData>(path: string, data: T) =>
  addDoc(getCollectionRef(path), data);
